// The value-first onboarding's BI vocabulary (native-apps/shared/onboarding-flow/contract.json `bi`), shared 1:1 with
// iOS `ValueFirstBI` and Android `ValueFirstBi`. Each docked question and each answer card in the chat is one
// component: an `itemSeen` (198:6) when it is on screen, a `clickCta` (198:10) for every tap on it, with
// `additionalFields.clicked` naming the tap. Every event carries the conversation as its entity.
//
// No label the model wrote and no value the owner typed rides these events: the business card's details are
// read off their site and can name their staff and clients. They carry ids, keys, counts and statuses, and
// `message_id` is the way back to the words. Band, agent and toolkit names do ride them, as they already do on
// `band_offer_selected`.
//
// Pure, no runtime imports, so a test can load it without the app's dependency graph.

import type { BandOfferCardData } from './band-cards';
import type { GoalCardData } from './goal-card';
import type { BusinessCardData, ConnectMeanwhileCardData, TeamCardData } from './value-first-onboarding';

/** Every value-first event reports the chat as its page, on all three surfaces. */
export const VALUE_FIRST_BI_PAGE = 'chat';

/** For a `PageContextProvider` around the value-first pieces. */
export const VALUE_FIRST_PAGE_CONTEXT = { pageName: VALUE_FIRST_BI_PAGE };

export const VALUE_FIRST_COMPONENT = {
  aboutTheBusiness: 'survey-modal-about-the-business',
  websiteOrSocialLink: 'survey-modal-website-or-social-link',
  chooseGoal: 'survey-modal-choose-goal',
  connectYourTool: 'survey-modal-connect-your-tool',
  /** The harness's survey at step `task` ("Not now, just help me with one thing"); every other survey stays `survey_modal`. */
  startFromTask: 'survey-modal-start-from-task',
  yourBusiness: 'your-business',
  yourFirstGoal: 'your-first-goal',
  yourTeam: 'your-team',
} as const;

/** `additionalFields.clicked` on a value-first click. */
export const VALUE_FIRST_CLICKED = {
  lookAtSite: 'look_at_site',
  businessChip: 'business_chip',
  wixSite: 'wix_site',
  link: 'link',
  back: 'back',
  goalOption: 'goal_option',
  notNow: 'not_now',
  skip: 'skip',
  connect: 'connect',
  connectAll: 'connect_all',
  edit: 'edit',
} as const;

/** The survey's component name: the onboarding's own at `task`, the survey card's everywhere else. */
export function valueFirstSurveyComponent(valueFirst: boolean, step: string | null | undefined, fallback: string): string {
  return valueFirst && step === 'task' ? VALUE_FIRST_COMPONENT.startFromTask : fallback;
}

/** `valueFirst.business.kind.yoga` → `yoga`: the chip's key, without the catalog prefix. */
export function businessChipId(key: string): string {
  return key.slice(key.lastIndexOf('.') + 1);
}

const list = (values: readonly (string | undefined)[]) => values.filter((v): v is string => !!v).join(',');

/** The business question: "Look at my site" and the business chips, in the order drawn. */
export function businessQuestionSeenFields(chipKeys: readonly string[]): Record<string, string> {
  return { options: list([VALUE_FIRST_CLICKED.lookAtSite, ...chipKeys.map(businessChipId)]) };
}

/** The site picker: the owner's Wix sites it offered, by metaSiteId. */
export function sitePickerSeenFields(sites: readonly { metaSiteId?: string }[]): Record<string, string> {
  return { site_count: String(sites.length), site_ids: list(sites.map((site) => site.metaSiteId)) };
}

/** The goal question: the bands it offered and the one marked "Best fit". */
export function goalQuestionSeenFields(card: BandOfferCardData): Record<string, string> {
  return {
    option_count: String(card.options.length),
    option_ids: list(card.options.map((option) => option.id)),
    option_names: list(card.options.map((option) => option.name)),
    best_fit_option_id: card.options.find((option) => option.bestFit)?.id ?? '',
  };
}

/** The connect card: the apps it offered and the ones already connected. `placement` is the dock or the chat. */
export function connectSeenFields(card: ConnectMeanwhileCardData, connected: (toolkit: string) => boolean, docked: boolean): Record<string, string> {
  return {
    placement: docked ? 'dock' : 'chat',
    toolkits: list(card.apps.map((app) => app.toolkit)),
    connected_toolkits: list(card.apps.filter((app) => connected(app.toolkit)).map((app) => app.toolkit)),
  };
}

/** The business card: where it was read from and which details it shows, never their values. */
export function businessCardSeenFields(card: BusinessCardData): Record<string, string> {
  const details = card.details ?? [];
  return {
    source: card.source ?? '',
    has_location: String(!!card.location),
    detail_count: String(details.length),
    detail_labels: list(details.map((detail) => detail.label)),
  };
}

/** The first goal: which goal, its tasks' statuses in order, and who runs them. */
export function goalCardSeenFields(card: GoalCardData): Record<string, string> {
  const tasks = card.tasks ?? [];
  return {
    goal_id: card.goalId ?? '',
    goal_slug: card.slug ?? '',
    task_count: String(tasks.length),
    task_statuses: list(tasks.map((task) => task.status)),
    agents: list([...new Set(tasks.map((task) => task.agent))]),
  };
}

/** The team: who is on it. */
export function teamCardSeenFields(card: TeamCardData): Record<string, string> {
  return {
    agent_count: String(card.agents.length),
    agent_ids: list(card.agents.map((agent) => agent.id)),
    agents: list(card.agents.map((agent) => agent.name)),
  };
}
